import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useClientV2} from "../../../utils/hooks";
import {AxiosError} from "axios";
import {
  CoachCancelUpcomingCallPayload,
  CustomerCancelUpcomingCallPayload,
  CustomerRescheduleUpcomingCallPayload,
  GetCoachUpcomingCallsApiResponse,
  GetCustomerUpcomingCallsApiResponse,
} from "./types";
import {
  normalizeV2CoachUpcomingCallsResponse,
  normalizeV2CustomerUpcomingCallsMeta,
  normalizeV2CustomerUpcomingCallsResponse,
} from "./upcoming-calls-normalizer";
import {message} from "antd";
import {AnalyticsEvent} from "../../../utils/analytics";
import {PaginationMeta} from "../../../redux/service/i-service";
import {createBookingActionsRepository} from "../../../features/booking-actions";

export const upcomingCallsQueryKey = "coach_upcoming_calls";
export const customerUpcomingCallsQueryKey = "customer_upcoming_calls";

export const useGetCoachUpcomingCalls = () => {
  const client = useClientV2();

  return useQuery<
    GetCoachUpcomingCallsApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [upcomingCallsQueryKey],
    queryFn: async () => {
      const response = await client("bookings/upcoming");
      const payload = response?.data ?? response;
      return normalizeV2CoachUpcomingCallsResponse(payload);
    },
    refetchInterval: 300000,
    // refetchOnWindowFocus: (q) => {
    //   if (q.) {
    //     return true;
    //   } else {
    //     return false;
    //   }
    // },
  });
};
export const useCoachCancelUpcomingCall = () => {
  const queryClient = useQueryClient();
  const repository = createBookingActionsRepository();
  return useMutation({
    mutationFn: (payload: CoachCancelUpcomingCallPayload) =>
      repository.cancelBooking({
        actor: "coach",
        reference: payload.bookingReference,
        reason: payload.reasonForCancellation,
      }),
    onMutate: () => {
      const oldBookings = queryClient.getQueryData([upcomingCallsQueryKey]);

      return () => queryClient.setQueryData([upcomingCallsQueryKey], oldBookings);
    },
    onError: (error: AxiosError<{message: string}>, values, rollback) => {
      message.error(error.message);
      AnalyticsEvent("Coach cancel booking", "Coach cancel booking fail");
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: (d, v) => {
      AnalyticsEvent("Coach cancel booking", "Coach cancel booking success");
      queryClient.removeQueries({
        exact: true,
        queryKey: ["customer_booking", String(v.bookingReference)],
      });
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: [upcomingCallsQueryKey]}),
  });
};

export const useGetCustomerUpcomingCalls = (page: number = 1, per_page: number = 5) => {
  const client = useClientV2();

  return useQuery<
    {data: GetCustomerUpcomingCallsApiResponse; meta: PaginationMeta},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [customerUpcomingCallsQueryKey],
    queryFn: async () => {
      const response = await client(
        `customer/bookings/upcoming?page=${page}&perPage=${per_page}`
      );
      const payload = response?.data ?? response;
      return {
        data: normalizeV2CustomerUpcomingCallsResponse(payload),
        meta: normalizeV2CustomerUpcomingCallsMeta(payload, page, per_page),
      };
    },
    keepPreviousData: true,
  });
};

export const useCustomerCancelUpcomingCall = () => {
  const queryClient = useQueryClient();
  const repository = createBookingActionsRepository();
  return useMutation({
    mutationFn: (payload: CustomerCancelUpcomingCallPayload) =>
      repository.cancelBooking({
        actor: "customer",
        reference: payload.bookingReference,
        reason: payload.reasonForCancellation,
      }),
    onMutate: () => {
      const oldBookings = queryClient.getQueryData([customerUpcomingCallsQueryKey]);

      return () => queryClient.setQueryData([customerUpcomingCallsQueryKey], oldBookings);
    },
    onError: (error: AxiosError<{message: string}>, values, rollback) => {
      message.error(error.message);
      AnalyticsEvent("Customer cancel booking", "Customer cancel booking fail");
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: () => {
      message.success("The selected booking has been cancelled successfully!");
      AnalyticsEvent("Customer cancel booking", "Customer cancel booking success");
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({queryKey: [customerUpcomingCallsQueryKey]}),
        queryClient.invalidateQueries({queryKey: ["customer_paid_bundles"]}),
      ]),
  });
};
export const useCustomerRescheduleUpcomingCall = () => {
  const queryClient = useQueryClient();
  const repository = createBookingActionsRepository();
  return useMutation({
    mutationFn: (payload: CustomerRescheduleUpcomingCallPayload) =>
      repository.rescheduleBooking({
        actor: "customer",
        reference: payload.bookingReference,
        bookingYear: payload.booking_year,
        bookingMonth: payload.booking_month,
        bookingDate: payload.booking_date,
        timeSlot: payload.timeSlot,
        reason: payload.description,
        mentorTimezone: payload.mentor_timezone,
        userTimezone: payload.user_timezone,
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("Coach reschedule booking", "Coach reschedule booking fail");
    },
    onSuccess: (d, v) => {
      AnalyticsEvent("Coach reschedule booking", "Coach reschedule booking success");
    },
    onSettled: () =>
      queryClient.invalidateQueries({queryKey: [customerUpcomingCallsQueryKey]}),
  });
};
